import React from "react";

const Services = () => {
  // Data structure for services to make code clean and scalable
  const servicesData = [
    {
      id: 1,
      title: "Web Development",
      desc: "Custom, responsive websites designed to meet government standards and accessibility requirements.",
      iconPath: "M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4", // Code Icon
      link: "/softwaredevelopment",
    },
    {
      id: 2,
      title: "Database Architecture",
      desc: "Secure, scalable, and high-performance database systems ensuring data integrity for critical operations.",
      iconPath:
        "M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4", // Database Icon
      link: "/softwaredevelopment",
    },
    {
      id: 3,
      title: "Enterprise Software",
      desc: "Tailored software solutions to streamline internal workflows and manage resources efficiently.",
      iconPath:
        "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10", // CPU/Box Icon
      link: "/softwaredevelopment",
    },
    {
      id: 4,
      title: "Internet Services Provider",
      desc: "Protecting sensitive government data with advanced encryption and compliance protocols.",
      iconPath:
        "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z", // Lock Icon
      link: "/isp",
    },
    {
      id: 5,
      title: "UI/UX Design",
      desc: "Creating intuitive interfaces that simplify complex tasks for administrators and the public.",
      iconPath:
        "M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122", // Cursor/Design Icon
      link: "/graphicdesign",
    },
    {
      id: 6,
      title: "IT Consultation",
      desc: "Expert advice on technology roadmaps to modernize legacy systems within government agencies.",
      iconPath:
        "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z", // Chart Icon
      link: "/softwaredevelopment",
    },
  ];

  return (
    // Section Container: Light slate background for contrast
    <section className="py-20 bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-blue-600 font-semibold tracking-wide uppercase text-sm mb-2">
            Our Expertise
          </h2>
          <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4">
            Comprehensive Tech Solutions
          </h3>
          <p className="text-lg text-slate-600 leading-relaxed">
            We provide end-to-end technology services designed to meet the
            rigorous standards of government and enterprise sectors.
          </p>
        </div>

        {/* Grid Layout for Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {servicesData.map((service) => (
            <div
              key={service.id}
              // Card Styles: White bg, rounded corners, shadow, hover lift effect
              className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl border border-slate-100 transition-all duration-300 hover:-translate-y-1 group"
            >
              {/* Icon Container */}
              <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                <svg
                  className="w-7 h-7"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d={service.iconPath}
                  />
                </svg>
              </div>

              {/* Service Title */}
              <h4 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">
                {service.title}
              </h4>

              {/* Service Description */}
              <p className="text-slate-600 mb-6 leading-relaxed h-20">
                {service.desc}
              </p>

              {/* Read More Link */}
              <a
                href={service.link}
                className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-800 transition-colors"
              >
                Learn More
                <svg
                  className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </a>
            </div>
          ))}
        </div>

        {/* Bottom CTA for Services */}
        <div className="mt-16 text-center">
          <a
            href="/services"
            className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-lg hover:shadow-blue-500/30 transition-all duration-300"
          >
            View All Services
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;
